import { Box, Card, CircularProgress, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';

import AttachmentPreviewDialog from '~/components/AttachmentPreviewDialog';
import AttachmentUploader from '~/components/AttachmentUploader';
import PdfPreviewWithActions from '~/components/documentPreview/PdfPreviewWithActions';
import { UploadDocumentType } from '~/components/uploadDocument/AddManuallyDropdown';
import { mapInvoiceToFormValues } from '~/components/uploadDocument/InvoiceForm';
import type { LiftAIDocumentTagged } from '~/components/uploadDocument/SelectDocumentTypeDialog';
import SelectUploadDocumentTypeDialog from '~/components/uploadDocument/SelectDocumentTypeDialog';
import {
  UploadDocumentDialogFlowContext,
  useUploadDocumentFlow,
} from '~/components/uploadDocument/UploadDocumentDialogFlow';
import { invoicesAttachmentsDestroy } from '~/data/generated/endpoints/invoices/invoices';
import { ticketsAttachmentsDestroy } from '~/data/generated/endpoints/tickets/tickets';
import { getInvoiceMutator } from '~/data/hooks/utils';
import {
  byPageNumber,
  isLinkedPDFAttachment,
  isOriginalDocumentPage,
} from '~/data/schemas/attachment';
import type {
  Invoice,
  InvoiceAttachment,
  InvoiceForm,
  InvoiceKindKey,
} from '~/data/schemas/invoice';
import { InvoiceKind } from '~/data/schemas/invoice';
import type { Ticket, TicketAttachment } from '~/data/schemas/ticket';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { CARD_SX } from '~/theme';

type LinkedAttachmentItem =
  | ({
      kind?: InvoiceKindKey;
      isTicket: false;
      ticketId?: never;
      // The owning invoice's id — the current invoice for its own attachments,
      // or the related invoice for attachments surfaced from relatedInvoices.
      invoiceId: string;
    } & InvoiceAttachment)
  | ({ kind?: never; isTicket: true; ticketId: string; invoiceId?: never } & TicketAttachment);
interface IAttachmentItemProps {
  item: LinkedAttachmentItem;
  onDelete: (id: string) => void;
  currentlyDeleting: { isLoading: boolean; id: string };
}

const getSubtitle = (item: LinkedAttachmentItem) => {
  if (item.isTicket) {
    return item.isNextPage ? UploadDocumentType.ticketContinued : UploadDocumentType.ticket;
  }

  // A continuation page carries `isNextPage`, with `isPrimary` set or not depending
  // on which writer produced it (see `isOriginalDocumentPage`). Gating on `isPrimary`
  // alone reached the "(Continued)" labels below only for the rows
  // `SupportingDataForm` writes, and filed every pipeline-written continuation page
  // under Supporting Data instead (AMP-722).
  if (item.isPrimary || item.isNextPage) {
    if (!item.isTicket && item.isVoid) {
      return UploadDocumentType.supportingData;
    }

    if (item.kind === InvoiceKind.Invoice) {
      return item.isNextPage ? UploadDocumentType.invoiceContinued : UploadDocumentType.invoice;
    } else if (item.kind === InvoiceKind.Proposal) {
      return item.isNextPage ? UploadDocumentType.proposalContinued : UploadDocumentType.proposal;
    } else if (item.kind === InvoiceKind.Credit) {
      return UploadDocumentType.credit;
    }
  }

  return UploadDocumentType.supportingData;
};

const AttachmentItem = ({ item, onDelete, currentlyDeleting }: IAttachmentItemProps) => {
  const { isLoading, id } = currentlyDeleting;
  const subtitle = getSubtitle({ ...item });
  const [openPreview, setOpenPreview] = useState(false);

  /** TODO: render <img> for fileType = image */
  return isLinkedPDFAttachment(item) ? (
    // No margin of its own — the grid owns the spacing, and the old `.5rem 1rem`
    // fought it, leaving uneven gutters between cards.
    <Box
      sx={{
        p: 0,
        height: '100%',
        textAlign: 'center',
        color: 'text.secondary',
        ...CARD_SX,
        overflow: 'hidden',
      }}
    >
      {isLoading && id === item.id ? (
        <Card
          sx={{
            height: 200,
            display: 'flex',
            backgroundColor: '#eee',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Card>
      ) : (
        <>
          <PdfPreviewWithActions
            fileId={item.id}
            filePath={item.attachmentUrl ?? undefined}
            pageNumber={item.pageNumber}
            subtitle={subtitle}
            onDeleted={onDelete}
            onPreview={() => setOpenPreview(true)}
          />
          <AttachmentPreviewDialog
            open={openPreview}
            filePath={item.attachmentUrl ?? undefined}
            pageNumber={item.pageNumber}
            onClose={() => setOpenPreview(false)}
          />
        </>
      )}
    </Box>
  ) : null;
};

/**
 * The invoice's own attachment cards: its primary pages, then its supporting
 * data, then the primary pages of the invoices linked to it.
 */
const selectInvoiceAttachmentItems = (invoice?: Invoice): LinkedAttachmentItem[] => {
  const invoicePages = (invoice?.attachments ?? [])
    .map((attachment) => ({
      ...attachment,
      invoiceId: invoice?.id ?? '',
      isTicket: false,
      kind: invoice?.kind,
    }))
    .filter(isOriginalDocumentPage)
    .sort(byPageNumber);
  const supportingDataPages = (invoice?.attachments ?? [])
    .map((sd) => ({ ...sd, invoiceId: invoice?.id ?? '', isTicket: false }))
    .filter((a) => !isOriginalDocumentPage(a))
    .reverse();
  const relatedInvoicePages = (invoice?.relatedInvoices ?? []).flatMap((relatedInvoice) =>
    relatedInvoice.attachments
      .map((attachment) => ({
        ...attachment,
        invoiceId: relatedInvoice.id ?? '',
        kind: relatedInvoice.kind,
        isTicket: false,
      }))
      .filter(isOriginalDocumentPage)
      .sort(byPageNumber),
  );

  return [
    ...invoicePages,
    ...supportingDataPages,
    ...relatedInvoicePages,
  ] as LinkedAttachmentItem[];
};

/** The cards for what is attached to the tickets this invoice covers. */
const selectTicketAttachmentItems = (tickets?: Ticket[]): LinkedAttachmentItem[] =>
  (
    tickets?.flatMap(({ attachments, id }) => {
      const ticketId: string = id ?? '';

      return (attachments ?? []).map((a) => ({
        ...a,
        ticketId,
        isTicket: true,
      })) as LinkedAttachmentItem[];
    }) ?? []
  ).reverse();

/**
 * How many documents the tab holds — what its "Attached documents (n)" heading
 * counts, so the chip on the tab label says the same number.
 */
export const countLinkedAttachments = (invoice?: Invoice, tickets?: Ticket[]): number =>
  selectInvoiceAttachmentItems(invoice).length + selectTicketAttachmentItems(tickets).length;

interface IAttachmentsProps {
  invoice?: Invoice;
  tickets?: Ticket[];
}

/** Card bounds. A page preview reads at roughly this size; wider is just stretched. */
const CARD_MIN_WIDTH = 220;
const CARD_MAX_WIDTH = 300;

const Attachments = ({ invoice, tickets }: IAttachmentsProps) => {
  const invoiceAttachmentsState = useMemo(() => selectInvoiceAttachmentItems(invoice), [invoice]);

  const ticketsAttachmentsState = useMemo(() => selectTicketAttachmentItems(tickets), [tickets]);

  const attachmentCount = invoiceAttachmentsState.length + ticketsAttachmentsState.length;

  const { showEntityActionSnackbar } = useLAISnackbar();
  const { mutate } = useSWRConfig();
  const [currentlyDeleting, setCurrentlyDeleting] = useState({ isLoading: false, id: '' });

  const onDeleteFactory = (type: 'invoice' | 'ticket') => {
    return async (attachmentId: string) => {
      const currentInvoiceId = invoice?.id ?? '';
      // Delete via the attachment's OWNING invoice — the current one for its own
      // attachments, the related invoice for attachments from relatedInvoices.
      const owningInvoiceId =
        invoiceAttachmentsState.find((a) => a.id === attachmentId)?.invoiceId ?? currentInvoiceId;
      const ticketId = ticketsAttachmentsState.find((t) => t.id === attachmentId)?.ticketId ?? '';

      try {
        setCurrentlyDeleting({ isLoading: true, id: attachmentId });

        if (type === 'invoice') {
          await invoicesAttachmentsDestroy(owningInvoiceId, attachmentId);
        } else {
          await ticketsAttachmentsDestroy(ticketId, attachmentId);
        }

        // Refresh the current invoice view (which re-fetches its relatedInvoices).
        void mutate(getInvoiceMutator(currentInvoiceId));

        showEntityActionSnackbar(
          {
            name: 'Attachment',
            action: ActionEnum.Delete,
          },
          {
            variant: 'info',
          },
        );
      } catch (error) {
        console.error('Error deleting attachment', error);
        showEntityActionSnackbar(
          {
            name: 'Deleting Attachment',
            action: ActionEnum.Fail,
          },
          {
            variant: 'error',
          },
        );
      } finally {
        setCurrentlyDeleting({ isLoading: false, id: attachmentId });
      }
    };
  };

  const uploadDocumentFlowProvider = useUploadDocumentFlow();
  const { state, onFilesUploaded, onDocumentFlowDone, goBack } = uploadDocumentFlowProvider;
  const invoiceFormData = useMemo(
    () => (invoice ? mapInvoiceToFormValues(invoice) : ({} as InvoiceForm)),
    [invoice],
  );

  const onDone = useCallback(
    async (docs: LiftAIDocumentTagged[]) => {
      onDocumentFlowDone(docs);

      if (invoice?.id) {
        void mutate(getInvoiceMutator(invoice.id));
      }
    },
    [invoice?.id, mutate, onDocumentFlowDone],
  );

  const onCancel = useCallback(async () => {
    goBack();
  }, [goBack]);

  return (
    <UploadDocumentDialogFlowContext.Provider value={uploadDocumentFlowProvider}>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: 3,
          overflowY: 'auto',
          bgcolor: 'grey.50',
        }}
      >
        <Box mb={3}>
          <AttachmentUploader
            fileSizeLimit={5 /*MB*/}
            acceptedFileTypes={{ 'application/pdf': ['.pdf'] }}
            acceptedFileTypesLabel="PDF"
            onFilesUploaded={onFilesUploaded}
          />
        </Box>
        {state.name === 'processDocuments' ? (
          <SelectUploadDocumentTypeDialog
            open
            onCancel={onCancel}
            onDone={onDone}
            documents={state.documents}
            editInvoice
            invoiceData={invoiceFormData}
          />
        ) : null}

        {attachmentCount === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No attachments yet.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
              Attached documents ({attachmentCount})
            </Typography>
            {/* Auto-fill rather than breakpoint columns: as many cards per row as the width
                allows, each keeping a page's proportions. It measures the container, so the drawer
                gets the same rule. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, ${CARD_MAX_WIDTH}px))`,
                gap: 2,
              }}
            >
              {invoiceAttachmentsState.map((attachment) => (
                <AttachmentItem
                  key={attachment.id}
                  currentlyDeleting={currentlyDeleting}
                  item={attachment}
                  onDelete={onDeleteFactory('invoice')}
                />
              ))}
              {ticketsAttachmentsState.map((attachment) => (
                <AttachmentItem
                  key={attachment.id}
                  currentlyDeleting={currentlyDeleting}
                  item={attachment}
                  onDelete={onDeleteFactory('ticket')}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </UploadDocumentDialogFlowContext.Provider>
  );
};

export default Attachments;
